import {
    Button,
    Dropdown,
    DropdownProps,
    Input,
    makeStyles,
    Option,
    shorthands,
    tokens,
} from '@fluentui/react-components';
import React from 'react';
import { KpiPanel, KpiPanelProps } from './KpiPanel';
import { LogEventType } from '../../libs/services/LogService';
import { SharedStyles } from '../../styles';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        backgroundColor: tokens.colorNeutralBackground1,
    },
    header: {
        ...shorthands.borderBottom('1px', 'solid', 'rgb(0 0 0 / 10%)'),
        ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
        backgroundColor: tokens.colorNeutralBackground1,
        display: 'flex',
        flexDirection: 'row',
        boxSizing: 'border-box',
        width: '100%',
        justifyContent: 'space-between',
    },
    title: {
        ...shorthands.gap(tokens.spacingHorizontalM),
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'row',
        fontWeight: tokens.fontWeightSemibold,
    },
    controls: {
        display: 'flex',
        alignItems: 'center',
        ...shorthands.gap('4px'),
    },
    scroll: {
        display: 'flex',
        flexWrap: 'wrap',
        ...shorthands.gap('12px'),
        ...shorthands.margin(tokens.spacingVerticalXS),
        ...SharedStyles.scroll,
    },
    info: {
        ...shorthands.margin('auto'),
    },
});

export interface KpiDefinition {
    type: LogEventType;
    label: string;
    dimension?: string;
    secondaryDimension?: string;
    value: string;
    showChart: boolean;
    sumValueByDate: boolean;
}

const KpiList: KpiDefinition[] = [
    {
        type: LogEventType.SessionCreated,
        label: 'Neue Chats',
        dimension: 'Benutzer',
        value: 'Anzahl',
        showChart: true,
        sumValueByDate: true
    },
    {
        type: LogEventType.MessageQueried,
        label: 'Nachricht geschickt',
        dimension: 'Benutzer',
        value: 'Anzahl',
        showChart: true,
        sumValueByDate: true
    },
    {type: LogEventType.UserActive, label: 'Aktive Benutzer', value: 'Anzahl', showChart: true, sumValueByDate: true},
    {
        type: LogEventType.PresetUsed,
        label: 'Hotbutton benutzt',
        dimension: 'Button',
        value: 'Anzahl',
        showChart: true,
        sumValueByDate: true
    },
    {
        type: LogEventType.ReferenceOpened,
        label: 'Referenz geöffnet',
        dimension: 'Referenz',
        value: 'Anzahl',
        showChart: true,
        sumValueByDate: true
    },
    {
        type: LogEventType.ReferenceUsed,
        label: 'Referenz für Antwort benutzt',
        dimension: 'Referenz',
        value: 'Anzahl',
        showChart: true,
        sumValueByDate: true
    },
    {
        type: LogEventType.TokenResponseTime,
        label: 'Zeit pro Antwort Token',
        value: 'Millisekunden',
        showChart: true,
        sumValueByDate: true
    },
    {
        type: LogEventType.NewsRead,
        label: 'News gelesen von Benutzer',
        dimension: 'News',
        value: 'Anzahl User',
        showChart: true,
        sumValueByDate: true
    },
    {
        type: LogEventType.NewsActivity,
        label: 'News-Aktivitäten',
        dimension: 'News',
        secondaryDimension: 'User',
        value: 'Aktion',
        showChart: false,
        sumValueByDate: false
    },
];

function formatDateString(date: Date): string {
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
}

function getMinimumDate(a: Date, b: Date): Date {
    return a < b ? a : b;
}

function getMaximumDate(a: Date, b: Date): Date {
    return a > b ? a : b;
}

export const KpiWindow: React.FC = () => {
    const classes = useClasses();

    const minDate = new Date();
    minDate.setDate(minDate.getDate() - 90);
    const maxDate = new Date();

    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);

    const [selectedKpi, setSelectedKpi] = React.useState<KpiDefinition | undefined>();
    const [fromDate, setFromDate] = React.useState(sevenDaysAgo);
    const [toDate, setToDate] = React.useState(maxDate);
    const [kpiPanelProps, setKpiPanelProps] = React.useState(null as KpiPanelProps | null);

    const onKpiSelect: DropdownProps['onOptionSelect'] = (_, data) => {
        setSelectedKpi(KpiList.find((i) => i.type === (data.optionValue as LogEventType)) ?? KpiList[0]);
    };

    const updateKpiPanel = () => {
        if (selectedKpi === undefined) return;

        const finalToDate = new Date(toDate.getTime());
        finalToDate.setDate(toDate.getDate() + 1);

        setKpiPanelProps({
            kpi: selectedKpi,
            from: fromDate,
            to: finalToDate,
        });
    };

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <div className={classes.title}>Statistiken</div>
                <div className={classes.controls}>
                    <Dropdown onOptionSelect={onKpiSelect} placeholder="Statistik auswählen">
                        {KpiList.map((i) => (
                            <Option key={i.type} value={i.type}>
                                {i.label}
                            </Option>
                        ))}
                    </Dropdown>
                    <Input
                        type="date"
                        contentBefore={<span>von</span>}
                        min={formatDateString(minDate)}
                        max={formatDateString(getMinimumDate(maxDate, toDate))}
                        value={formatDateString(fromDate)}
                        onChange={(_, data) => {
                            if (data.value === '') {
                                setFromDate(getMinimumDate(sevenDaysAgo, toDate));
                                return;
                            }
                            const newDate = new Date(`${data.value}`);
                            setFromDate(newDate);
                        }}
                    />
                    <Input
                        type="date"
                        contentBefore={<span>bis</span>}
                        min={formatDateString(getMaximumDate(minDate, fromDate))}
                        max={formatDateString(maxDate)}
                        value={formatDateString(toDate)}
                        onChange={(_, data) => {
                            if (data.value === '') {
                                setToDate(getMaximumDate(today, toDate));
                                return;
                            }
                            const newDate = new Date(`${data.value}`);
                            setToDate(newDate);
                        }}
                    />
                    <Button appearance="primary" onClick={updateKpiPanel} disabled={selectedKpi === undefined}>
                        Anzeigen
                    </Button>
                </div>
            </div>
            <div className={classes.scroll}>
                {kpiPanelProps && <KpiPanel kpi={kpiPanelProps.kpi} from={kpiPanelProps.from} to={kpiPanelProps.to} />}
                {!kpiPanelProps && (
                    <div className={classes.info}>
                        Gewünschte Statistik und Zeitraum angeben, und dann auf Anzeigen klicken.
                    </div>
                )}
            </div>
        </div>
    );
};
